import React, {useMemo} from 'react'
import {Flex, useBreakpointValue} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import Slider from '../../components/slider'
import WidgetStoreTile from './widget-store-tile'

/**
 * Display store tile for a given store ID.
 *
 * @param {string} stores - object with storeIds and alignment for text
 *
 * @param {string} color - backgroundcolor for StoreTiles
 *
 * @param {string} images - yes or no - should images be shown or not
 *
 * @example
 *   <pwa-widget-store stores="[{"id": "001D000000mYr6ZIAS", "alignment": "right"},{"id": "001D000000mYr6ZIAS", "alignment": "left"}]" color="pink" images="yes">
 *     $include('Widget-Store', 'sid', '#(store.store_id)')$
 *   </pwa-widget-product>
 */
const WidgetStore = ({stores, color, images}) => {
    const isSm = useBreakpointValue({base: true, md: false})
    const itemsPerSlide = useBreakpointValue({base: 1, md: 2, lg: 3, '2xl': 4})
    const showImages = images === 'yes'

    function tryParseJSONObject(jsonString) {
        try {
            const o = JSON.parse(jsonString)
            if (o && typeof o === 'object') {
                return o
            }
        } catch (e) {
            console.error('Error parsing stores from cms', e)
            return null
        }
    }

    const tiles = useMemo(() => {
        if (stores) {
            const storesJson = tryParseJSONObject(stores)
            if (storesJson) {
                if (!isSm) {
                    return storesJson
                } else {
                    const out = []
                    storesJson.forEach((item, index) => {
                        if (index % 2 === 0) {
                            out.push([item, storesJson[index + 1]])
                        }
                    })
                    return out
                }
            } else {
                return null
            }
        }
    }, [stores, isSm])

    if (!tiles) {
        return null
    }

    return (
        <Slider itemsPerSlide={itemsPerSlide}>
            {isSm
                ? tiles.map((store) => {
                      return (
                          <Flex key={store[0].id} gap={4} flexDirection="column">
                              <WidgetStoreTile
                                  key={store[0].id}
                                  id={store[0].id}
                                  color={color}
                                  showImages={showImages}
                                  variant={store[0].alignment}
                              />
                              {!!store[1] && (
                                  <WidgetStoreTile
                                      key={store[1].id}
                                      id={store[1].id}
                                      color={color}
                                      showImages={showImages}
                                      variant={store[1].alignment}
                                  />
                              )}
                          </Flex>
                      )
                  })
                : tiles.map((store) => {
                      return (
                          <WidgetStoreTile
                              key={store.id}
                              id={store.id}
                              color={color}
                              showImages={showImages}
                              variant={store.alignment}
                          />
                      )
                  })}
        </Slider>
    )
}

WidgetStore.propTypes = {
    stores: PropTypes.string,
    color: PropTypes.string,
    images: PropTypes.string
}

export default WidgetStore
